import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import { APRYZE_STATIC_URL } from '@smx-ui-common/consts';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { SmxPermissionContext } from '@smx-ui-permission/provider/permission-provider';
import { PermissionCombinationType } from '@smx-ui-permission/types';
import { Spin } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';

import { API } from '../../../../service/drawings/src/ui/common';
import { DrawingSheetOrDocumentIdDataType } from '../../../../service/drawings/src/ui/types/types';
import { UI_ELEMENTS } from './constants/ui-elements';
import { useAnnotationViewerAppryze } from './hook/use-annotation-viewer-appryze';
import { useStateViewerAppryze } from './hook/use-state-viewer-appryze';

type BufferAnnotationItemType = {
    fn: () => Promise<void>;
    action: 'add' | 'modify' | 'delete';
};

// Global suppression of Apryse errors
if (typeof window !== 'undefined') {
    const originalConsoleError = console.error;
    console.error = (...args: any[]) => {
        const message = args[0]?.toString() || '';
        const stack = args[0]?.stack || '';
        if (
            message.includes('exposedSelectors') ||
            message.includes('Cannot read properties of undefined') ||
            message.includes('webviewer-core') ||
            stack.includes('webviewer-core') ||
            stack.includes('exposedSelectors')
        ) {
            return;
        }
        originalConsoleError.apply(console, args);
    };

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const message = event.reason?.message || event.reason?.toString() || '';
        const stack = event.reason?.stack || '';
        if (
            message.includes('Cannot read properties of undefined') ||
            message.includes('forEach') ||
            stack.includes('webviewer-core') ||
            stack.includes('exposedSelectors')
        ) {
            event.preventDefault();
            event.stopPropagation();
        }
    });

    window.addEventListener('error', (event: ErrorEvent) => {
        const message = event.message || '';
        const filename = event.filename || '';
        if (
            message.includes('Cannot read properties of undefined') ||
            filename.includes('webviewer-core') ||
            filename.includes('exposedSelectors')
        ) {
            event.preventDefault();
            event.stopPropagation();
        }
    });
}

export type ViewerAppryzeContextType = 'drawing' | 'document' | 'attachmentScheduleItem' | 'attachmentTask';

export type ViewerAppryzeProps = {
    file: string | File | null;
    fileName?: string;
    height?: string;
    toolBar?: (instance: WebViewerInstance) => void;
    sheetData?: DrawingSheetOrDocumentIdDataType;
    displaySpinner?: boolean;
    context: ViewerAppryzeContextType;
    allowedAnnotation?: boolean;
    bufferAnnotation?: boolean;
    onSave?: () => void;
    onUpdateAnnotations?: (flag: boolean) => void;
    triggerSave?: boolean;
};

export const getPermissionsList = (context: ViewerAppryzeContextType): PermissionCombinationType[] => {
    switch (context) {
        case 'drawing':
            return ['drawing.manage'];
        case 'document':
            return ['documents.manage'];
        case 'attachmentScheduleItem':
            return ['work-item.manage'];
        case 'attachmentTask':
            return ['schedule-task.manage'];
        default:
            return [];
    }
};

const TYPE_IMAGE = 'image';

const TYPE_NONE = 'none';

export type ViewerAppryzeHandle = { save: () => void };

export const ViewerAppryze: React.FC<ViewerAppryzeProps> = ({
    file,
    fileName,
    toolBar,
    sheetData,
    height = '186px',
    context,
    allowedAnnotation = true,
    bufferAnnotation = false,
    displaySpinner = false,
    onSave = () => {},
    onUpdateAnnotations = () => {},
    triggerSave = false,
}) => {
    const webViewerRef = useRef<HTMLDivElement>(null);
    const isViewerInitialized = useRef<boolean>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [webViewer, setWebViewer] = useState<WebViewerInstance | null>(null);
    const intervalValueRef = useRef<number | NodeJS.Timeout>(0);
    const bufferAnnotationListRef = useRef<{ [key: string]: BufferAnnotationItemType[] }>({});

    const { setupAnnotationInterception, setCurrentDrawingSheetOrDocumentId } = useAnnotationViewerAppryze(
        webViewer,
        sheetData,
        bufferAnnotation,
        bufferAnnotationListRef,
        onUpdateAnnotations,
    );

    const { displayName } = useContext(SmxPermissionContext);

    const { setupStateListeners } = useStateViewerAppryze(webViewer);

    const fullDomain = window.location.origin;

    const permission = getPermissionsList(context);

    const accessManager = useAccess(permission);

    if (!allowedAnnotation) {
        toolBar = undefined;
    }

    useEffect(() => {
        let instance: WebViewerInstance | null = null;
        let cleanupStateListeners: (() => void) | null = null;
        let cleanupAnnotationInterception: (() => void) | null = null;
        let onAnnotationSelectedHandler: ((annotations: Core.Annotations.Annotation[]) => void) | null = null;
        let saveModalObserver: MutationObserver | null = null;
        let cleanupDocumentLoaded: (() => void) | null = null;

        const initWebViewer = async () => {
            if (!webViewerRef.current) {
                return;
            }

            // setLoading(true);
            try {
                instance = await WebViewer.WebComponent(
                    {
                        licenseKey: API.APRYSE_LICENSE,
                        path: APRYZE_STATIC_URL,

                        isAdminUser: accessManager,
                        annotationUser: displayName,
                        enableAnnotations: true,
                        // fullAPI: true,
                        enableMeasurement: true,
                        css: accessManager ? `${fullDomain}/style-manager.css` : `${fullDomain}/style.css`,
                        //
                        // isReadOnly: true,
                        // enableRedaction: true,
                        // enableOptimizedWorkers: true,
                    },
                    webViewerRef.current,
                );

                instance.Core.annotationManager.enableRedaction();

                instance.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);

                const { documentViewer } = instance.Core;
                const onDocumentLoaded = () => {
                    const document = documentViewer.getDocument();
                    if (document && document.getType() === TYPE_IMAGE && instance) {
                        instance.UI.disableElements([UI_ELEMENTS.DOWNLOAD_BUTTON]);
                    }
                };
                documentViewer.addEventListener('documentLoaded', onDocumentLoaded);
                cleanupDocumentLoaded = () => {
                    documentViewer.removeEventListener('documentLoaded', onDocumentLoaded);
                };
                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL]);
                instance.UI.disableElements([UI_ELEMENTS.NOTES_PANEL_HEADER]);

                const setupHideIncludeComments = () => {
                    const host = webViewerRef.current?.querySelector('apryse-webviewer');
                    const root = host?.shadowRoot as ShadowRoot | null;
                    if (!root) return;

                    const removeOption = () => {
                        const inputs = root.querySelectorAll('input[name="include-comment-option"]');
                        inputs.forEach((input) => {
                            if (!(input instanceof HTMLInputElement)) return;
                            const choice = input.closest('.ui__choice');
                            if (choice instanceof HTMLElement) {
                                choice.style.display = TYPE_NONE;
                            } else {
                                input.style.display = TYPE_NONE;
                                const label = input.nextElementSibling;
                                if (label instanceof HTMLLabelElement) {
                                    label.style.display = TYPE_NONE;
                                }
                            }
                        });
                    };

                    removeOption();
                    saveModalObserver = new MutationObserver(() => removeOption());
                    saveModalObserver.observe(root, { childList: true, subtree: true });
                };
                setupHideIncludeComments();

                const annotManager = instance.Core.annotationManager;
                const ui = instance.UI;
                onAnnotationSelectedHandler = (annotations: Core.Annotations.Annotation[]) => {
                    const Sticky = instance?.Core?.Annotations?.StickyAnnotation as any;
                    const isNoteSelected =
                        Array.isArray(annotations) && annotations.some((a) => Sticky && a instanceof Sticky);

                    if (isNoteSelected) {
                        ui.enableElements([UI_ELEMENTS.NOTES_PANEL, UI_ELEMENTS.NOTES_PANEL_HEADER]);
                    } else {
                        ui.disableElements([UI_ELEMENTS.NOTES_PANEL, UI_ELEMENTS.NOTES_PANEL_HEADER]);
                    }
                };
                annotManager.addEventListener('annotationSelected', onAnnotationSelectedHandler);

                cleanupStateListeners = setupStateListeners(instance);
                cleanupAnnotationInterception = allowedAnnotation
                    ? setupAnnotationInterception(instance) || null
                    : null;

                setWebViewer(instance);
            } catch (error) {
                console.error('Error initializing WebViewer:', error);
            } finally {
                setTimeout(() => {
                    // setLoading(false);
                }, 500);
            }
        };

        if (!isViewerInitialized.current) {
            isViewerInitialized.current = true;
            initWebViewer();
        }

        return () => {
            cleanupStateListeners?.();
            cleanupAnnotationInterception?.();
            saveModalObserver?.disconnect();
            cleanupDocumentLoaded?.();

            if (instance) {
                const annotManager = instance.Core?.annotationManager;
                if (onAnnotationSelectedHandler) {
                    annotManager?.removeEventListener?.('annotationSelected', onAnnotationSelectedHandler);
                }
                instance.UI.closeDocument();
                setWebViewer(null);
                isViewerInitialized.current = false;
            }
        };
    }, []);

    useEffect(() => {
        if (!webViewer) return;

        if (toolBar) {
            toolBar(webViewer);
        } else {
            webViewer.UI.disableElements([UI_ELEMENTS.DEFAULT_TOP_HEADER]);
            webViewer.UI.disableElements([UI_ELEMENTS.TOOLS_HEADER]);
        }

        webViewer.UI.disableElements([UI_ELEMENTS.PAGE_NAV_FLOATING_HEADER]);
    }, [webViewer, toolBar]);

    useEffect(() => {
        if (!webViewer || !file) return;

        const loadDocument = () => {
            setLoading(true);
            try {
                webViewer.UI.loadDocument(file, {
                    filename: (file as File)?.name || fileName,
                });

                intervalValueRef.current = setInterval(() => {
                    if (webViewer.Core.documentViewer.getDocument()) {
                        if (allowedAnnotation) {
                            setCurrentDrawingSheetOrDocumentId(sheetData?.sheetId || sheetData?.documentId);
                        }

                        clearInterval(intervalValueRef.current);
                        setLoading(false);
                    }
                }, 200);
            } catch (error) {
                console.error('Error loading document:', error);
            }
        };

        loadDocument();
    }, [webViewer, file]);

    const handleSave = async () => {
        for (const key of Object.keys(bufferAnnotationListRef.current)) {
            let itemBuffer: BufferAnnotationItemType[] = bufferAnnotationListRef.current[key];
            if (bufferAnnotationListRef.current[key].length > 1) {
                const firstItem = bufferAnnotationListRef.current[key]?.[0];
                const lastItem = (bufferAnnotationListRef.current[key]?.[
                    bufferAnnotationListRef.current[key].length - 1
                ] || {}) as BufferAnnotationItemType;
                // Cases we need to skip
                if (!firstItem || (firstItem.action === 'add' && lastItem.action === 'delete')) {
                    continue;
                }

                // Modifications reduced to the last one
                if (firstItem.action === 'add' && lastItem.action === 'modify') {
                    itemBuffer = [firstItem, lastItem];
                } else if (lastItem.action === 'modify') {
                    itemBuffer = [lastItem];
                }
                // If delete, then only delete
                if (lastItem.action === 'delete') {
                    itemBuffer = [lastItem];
                }
            }
            for (const item of itemBuffer) {
                try {
                    await item.fn();
                    if (item.action === 'add') await new Promise((resolve) => setTimeout(resolve, 1000));
                } catch (error) {
                    console.error('Error saving annotation:', error);
                }
            }
        }
        onSave();
    };

    useEffect(() => {
        if (triggerSave === true) handleSave();
    }, [triggerSave]);

    return (
        <>
            <div
                ref={webViewerRef}
                style={{
                    width: '100%',
                    height,
                }}
            />
            {displaySpinner && <Spin spinning={loading} fullscreen={true}></Spin>}
        </>
    );
};
